import {Close, Search} from '@rsuite/icons'
import {isEmpty} from 'lodash'
import {useState, useMemo, useCallback, useEffect} from 'react'
import {FiEdit2, FiPlus, FiTrash} from 'react-icons/fi'
import {IconButton, Input, InputGroup, Pagination, Table, Tag, Tooltip, Whisper} from 'rsuite'
import RSuiteTable from 'src/cms/helpers/components/rsuiteTable'
import DeleteModal from '../../common/components/deleteModal'
import AddDocuments from './AddDocuments'
import {useGetDocuments} from './hooks/useGetDocuments'
import axios from 'axios'
import {API_URL} from 'src/cms/constants/EndPoint'
import {toast} from 'react-toastify'
import {useQueryClient} from 'react-query'

const Cell = Table.Cell

const Documents = () => {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [selectedId, setSelectedId] = useState<string>('')
  const [alertOpen, setAlertOpen] = useState(false)
  const [checkedValues, setCheckedValues] = useState<string | undefined>()
  const handleAlertOpen = useCallback(() => setAlertOpen(true), [])
  const handleAlertClose = useCallback(() => setAlertOpen(false), [])
  const [params, setParams] = useState({
    search: '',
    page: 1,
    limit: 10,
  })

  const {data: documentsData, isLoading: loading, refetch} = useGetDocuments(params)

  const handleChangeLimit = useCallback((dataKey: number) => {
    setParams((prev) => ({
      ...prev,
      limit: dataKey,
    }))
  }, [])

  const handleAddModal = useCallback(() => {
    setOpen(true)
    setSelectedId('')
  }, [])

  const handleEdit = useCallback((id: string) => {
    setOpen(true)
    setSelectedId(id)
  }, [])

  const handleDelete = useCallback(
    (id: string) => {
      setCheckedValues(id)
      handleAlertOpen()
    },
    [handleAlertOpen]
  )

  const modalSubmit = useCallback(async () => {
    try {
      await axios.delete(`${API_URL}/documents/${checkedValues}`)
      toast.success('Document deleted successfully')
      queryClient.invalidateQueries(['GET_DOCUMENTS'])
      handleAlertClose()
      refetch()
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error?.data?.data?.message ||
          error?.message ||
          'Failed to delete document'
      )
    }
  }, [checkedValues, queryClient, refetch, handleAlertClose])

  const ActionCell = useCallback(
    ({rowData, ...props}: any) => (
      <Cell {...props} className='link-group'>
        <Whisper placement='top' trigger='hover' speaker={<Tooltip>Edit</Tooltip>}>
          <IconButton
            appearance='subtle'
            onClick={() => handleEdit(rowData?.id)}
            icon={<FiEdit2 />}
          />
        </Whisper>
        <Whisper placement='top' trigger='hover' speaker={<Tooltip>Delete</Tooltip>}>
          <IconButton
            appearance='subtle'
            onClick={() => handleDelete(rowData?.id)}
            icon={<FiTrash />}
            data-cy='delete-button'
          />
        </Whisper>
      </Cell>
    ),
    [handleEdit, handleDelete]
  )

  const columns = [
    {
      label: 'S.N.',
      dataKey: 'sn',
      width: 50,
      cell: <Cell dataKey='sn' />,
    },
    {
      label: 'Document Name',
      dataKey: 'title',
      cell: <Cell dataKey='title' />,
      sortable: false,
      flexGrow: 1,
    },
    {
      label: 'Status',
      dataKey: 'status',
      width: 85,
      cell: (
        <Cell>
          {(rowData: any) =>
            rowData?.status ? (
              <Tag color='green' size='sm'>
                Active
              </Tag>
            ) : (
              <Tag color='red' size='sm'>
                Inactive
              </Tag>
            )
          }
        </Cell>
      ),
      sortable: false,
    },
    {
      label: 'Action',
      width: 200,
      align: 'center',
      cell: <ActionCell dataKey='id' />,
      fixed: 'right',
    },
  ]

  const tableData =
    documentsData?.data?.online_account_document?.map((item: any, index: number) => ({
      ...item,
      sn: index + 1,
    })) || []

  return (
    <div className='shadow p-3 bg-white rounded'>
      <div className='row g-0 border-bottom align-items-center mb-3 py-3'>
        <div className='col-lg-6'>
          <div className='heading__component__title fw-bolder'>Documents</div>
        </div>
        <div className='col-lg-6'>
          <div className='d-flex justify-content-end'>
            <div className='sub_button' onClick={handleAddModal}>
              <button className='btn btn-primary'>
                <FiPlus className='me-2' />
                Add
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className='d-flex justify-content-start align-item-center gap-4 amnil-table-toolbar'>
        <InputGroup inside style={{width: '30%', margin: '1rem 0'}}>
          <Input
            placeholder='Search'
            value={params?.search}
            onChange={(value) => setParams({...params, search: value})}
            data-cy='search-field'
          />
          <InputGroup.Addon>
            {!isEmpty(params?.search) ? (
              <Close
                style={{cursor: 'pointer'}}
                onClick={() => setParams({...params, search: ''})}
                data-cy='search-close-button'
              />
            ) : (
              <Search />
            )}
          </InputGroup.Addon>
        </InputGroup>
      </div>

      <div className='datatable'>
        <RSuiteTable columns={columns} showLoading={loading} data={tableData} />
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          size='sm'
          layout={['total', '-', 'limit', '|', 'pager', 'skip']}
          total={documentsData?.data?.meta?.total ?? 0}
          limitOptions={[10, 20, 50, 100]}
          limit={params?.limit}
          activePage={params?.page}
          onChangePage={(value) => setParams({...params, page: value})}
          onChangeLimit={handleChangeLimit}
        />
      </div>

      <AddDocuments open={open} handleClose={() => setOpen(false)} id={selectedId} />

      <DeleteModal handleClick={modalSubmit} isOpen={alertOpen} handleClose={handleAlertClose} />
    </div>
  )
}

export default Documents
